import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';
import {useTranslation} from 'react-i18next';

import {SectionHeader} from 'UI/components/layout/SectionHeader';
import {TourListItem} from 'UI/components/modules/TourListItem';

import {formatDuration, formatPrice, getCategory, getCoverUrl} from 'UI/hooks/useHomePage';

import {CATEGORY_ICON_MAP} from 'UI/utils/categoryIconMap';

import type {Walk} from 'types/payload-types';

/**
 * Props for the AllWalksBlock component.
 */
interface ComponentProps {
    /** Callback when a walk item is clicked. */
    onWalkClick?(slug: string): void;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     *
     * @default 'AllWalksBlock'
     */
    testId?: string;
    /** Section title. */
    title: string;
    /** Array of Walk documents to display (injected by the page). */
    walks?: Walk[];
}

/**
 * Renders a vertical list of all walks with title and tour list items.
 *
 * @param props             The component props.
 * @param props.onWalkClick Callback when a walk is clicked.
 * @param props.testId      An optional test ID. Defaults to `'AllWalksBlock'`.
 * @param props.title       The section title.
 * @param props.walks       Array of Walk documents to display.
 * @returns A JSX element rendering the all walks list.
 *
 * @example
 * ```tsx
 * <AllWalksBlock title="Alle Touren" walks={walks} />
 * ```
 */
const AllWalksBlock = ({onWalkClick, testId = 'AllWalksBlock', title, walks = []}: ComponentProps) => {
    const {t} = useTranslation();

    if (walks.length === 0) return null;

    return (
        <Section data-cy={testId}>
            <SectionHeader title={title} />
            <TourList>
                {walks.map(walk => {
                    const firstCat = walk.categories?.find(c => typeof c === 'object');
                    const IconComp = firstCat && typeof firstCat === 'object' && firstCat.predefinedIcon
                        ? CATEGORY_ICON_MAP[firstCat.predefinedIcon]
                        : null;

                    return (
                        <TourListItem
                            key={walk.id}
                            category={getCategory(walk, t)}
                            categoryIcon={IconComp ? <IconComp /> : undefined}
                            duration={formatDuration(walk)}
                            price={formatPrice(walk)}
                            thumbnailUrl={getCoverUrl(walk)}
                            title={walk.title}
                            onClick={() => onWalkClick?.(walk.slug)}
                        />
                    );
                })}
            </TourList>
        </Section>
    );
};

AllWalksBlock.displayName = 'AllWalksBlock';

export {AllWalksBlock};

// ── Styled ──────────────────────────────────────────────────

const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${spacing(6)};
`;

const TourList = styled.div`
    display: grid;
    gap: ${spacing(4)};
    grid-template-columns: 1fr;

    ${media('md')} {
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    }
`;